/**
 * 📣 行銷中心（2026-09-08 合二為一）：原「市場推廣」＋「行銷素材庫」合併。
 * 六 TAB：SHOW 三帖／30日日曆／對話語料／海報連結／賣點與重做評估／交付物原文庫。
 * 資料源＝marketing/*.md（Vite ?raw build 時嵌入）——repo 單一真相源，改檔重 build 即反映。
 * posters PNG 由 public/posters/ 供下載（源 marketing/posters/，更新後重 copy＋build）。
 */
import { useMemo, useState } from "react";
import showPostsMd from "../../../../marketing/show-posts-fb.md?raw";
import launchCampaignMd from "../../../../marketing/launch-campaign.md?raw";
import demoConversationsMd from "../../../../marketing/demo-conversations.md?raw";
import coreAdvantages from "../../../../marketing/core-advantages.md?raw";
import socialPlan from "../../../../marketing/social-media-plan.md?raw";
import pricing from "../../../../marketing/pricing-and-services.md?raw";
import videoAd from "../../../../marketing/video-ad-45s.md?raw";
import imageAds from "../../../../marketing/image-ads.md?raw";
import promptsMaster from "../../../../marketing/prompts-master-list.md?raw";
import systemPrompt from "../../../../marketing/system-prompt-test_tenant_001.md?raw";
import knowledgeBase from "../../../../marketing/knowledge-base-test_tenant_001.txt?raw";
import aipaCopy from "../../../../marketing/aipa/copy-pack.md?raw";

type AssetTab = "aipa" | "show" | "calendar" | "dialog" | "links" | "pitch" | "docs";

const TABS: { id: AssetTab; label: string }[] = [
  { id: "aipa", label: "✦ AIPA 個人助理" },
  { id: "show", label: "📱 SHOW 三帖" },
  { id: "calendar", label: "📅 30日日曆" },
  { id: "dialog", label: "💬 真實對話語料" },
  { id: "links", label: "🔗 海報與連結" },
  { id: "pitch", label: "🎯 賣點與重做評估" },
  { id: "docs", label: "📄 交付物原文庫" },
];

const POSTERS = [
  { file: "poster-1-pain.png", label: "P1 深夜漏客（凌晨1:47）" },
  { file: "poster-2-killer.png", label: "P2 手機照覆（殺手鐧）" },
  { file: "poster-3-cost.png", label: "P3 幫老闆計數" },
  { file: "poster-4-kb.png", label: "P4 知識庫即貼即學" },
  { file: "poster-5-boss.png", label: "P5 老闆視角" },
  { file: "chat-poster-1-areyou-bot.png", label: "chat-D2 你係咪bot" },
  { file: "chat-poster-2-mpf.png", label: "chat-D5 MPF 計數" },
  { file: "chat-poster-3-memory.png", label: "chat-D1 多輪記憶" },
  { file: "chat-poster-4-leakorder.png", label: "chat-D6 漏單共鳴" },
];

/** 絕對優勢（賣點主軸——所有對外语言由此出發） */
const ADVANTAGES = [
  { icon: "🥇", title: "用返自己個號碼，手機照回", desc: "AI 答嘅同時，老闆手機 WhatsApp 照樣收到、照樣回。Meta 官方 API / SleekFlow / WATI 都做唔到——佢哋要你搬入佢嘅 inbox。" },
  { icon: "🥈", title: "真人教一次，AI 永久學識", desc: "每次人工接管完，AI 自動將你嘅回覆整理成知識庫建議，後台一撳「採納」即入庫。每一次救火都變成資產。" },
  { icon: "🥉", title: "識就答、唔識就轉，唔會屈客", desc: "知識庫冇料會誠實認唔知，即刻通知你手機、暫停 30 分鐘等你接。寧願轉介都唔作料。" },
  { icon: "⚡", title: "15 分鐘對話式開通", desc: "免 Meta 商業驗證、免文件、免填表。同開通師傅傾 15 分鐘，prompt＋知識庫自動搞掂。" },
  { icon: "💰", title: "成本結構降維", desc: "Cloudflare 邊緣運算＋免費額度引擎分層，邊際成本接近零。平係架構決定嘅，唔係蝕本。" },
];

/** 交付物重做評估（原 2026-08-16；2026-09-08 按現況重評——已完成嘅標好，等 captain 一眼見到剩低乜） */
const REFRESH_PLAN = [
  { file: "aibot-landing.html", level: "done", action: "✅ 已完成：landing 已上線（marketing/landing-dist→Pages aibot-landing）並多輪迭代——三語／DSEG7 鐘／引導光效／SHOW 化 CTA；舊單檔版留檔唔使理" },
  { file: "pricing-and-services.md", level: "done", action: "✅ 已落地：定價終版 Lite HK$199／Pro HK$499＋/pricing 付款頁＋手動金流通車；本檔變歷史檔" },
  { file: "social-media-plan.md", level: "done", action: "✅ 已由 launch-campaign.md 30日日曆取代（更可執行：日／渠道／素材／歸因前綴）" },
  { file: "prompts-master-list.md", level: "tune", action: "大部分已補（師傅 v3/v4＝9/7 B2 節）；餘下新 prompt 陸續補錄" },
  { file: "knowledge-base-test_tenant_001.txt", level: "tune", action: "照舊規矩：新功能條目用 ingest-append 增量補，唔好覆蓋重傳" },
  { file: "system-prompt-test_tenant_001.md", level: "ok", action: "沿用（人格與紅線正確；demo KB 引擎口徑已修正）" },
  { file: "posters/chat-poster-*", level: "ok", action: "沿用——已入「🔗 海報與連結」TAB 可即下載" },
  { file: "demo-conversations.md", level: "warn", action: "仍待補：learning loop 全流程實錄（轉真人→控制台回覆→採納→AI 識答），放最前做王牌" },
  { file: "video-ad-45s.md", level: "warn", action: "仍待製作：45s 結構已寫好；Shot 5 可換 learning loop 實錄屏" },
];

const DOCS = [
  { key: "advantages", label: "🏆 核心訊息源（絕對優勢）", file: "core-advantages.md", content: coreAdvantages },
  { key: "show", label: "📱 SHOW 三帖文案", file: "show-posts-fb.md", content: showPostsMd },
  { key: "campaign", label: "📅 30日日曆＋直銷腳本＋歸因總表", file: "launch-campaign.md", content: launchCampaignMd },
  { key: "social", label: "📅 社交媒體推廣計劃（舊版留檔）", file: "social-media-plan.md", content: socialPlan },
  { key: "video", label: "🎬 45 秒影片方案", file: "video-ad-45s.md", content: videoAd },
  { key: "image", label: "🖼️ 圖片廣告方案", file: "image-ads.md", content: imageAds },
  { key: "pricing", label: "💰 收費模式（歷史檔——終版見 /pricing）", file: "pricing-and-services.md", content: pricing },
  { key: "prompt", label: "🎭 樣板 System Prompt（已上線）", file: "system-prompt-test_tenant_001.md", content: systemPrompt },
  { key: "kb", label: "📚 樣板知識庫（已上線）", file: "knowledge-base-test_tenant_001.txt", content: knowledgeBase },
  { key: "prompts", label: "🧾 提示詞總表", file: "prompts-master-list.md", content: promptsMaster },
];

const LEVEL_STYLE: Record<string, { badge: string; label: string }> = {
  warn: { badge: "bg-amber-500/20 text-amber-400", label: "需重做" },
  tune: { badge: "bg-sky-500/20 text-sky-400", label: "微調" },
  ok: { badge: "bg-emerald-500/20 text-emerald-400", label: "沿用" },
  done: { badge: "bg-emerald-900/40 text-emerald-300 border border-emerald-700/40", label: "✅ 已完成" },
};

function CopyBtn({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text).then(
          () => {
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          },
          () => {}
        );
      }}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
        done
          ? "bg-emerald-600/30 border-emerald-500 text-emerald-300"
          : "bg-slate-950/40 border-slate-600 text-slate-300 hover:border-emerald-500 hover:text-emerald-300"
      }`}
    >
      {done ? "✓ 已複製" : label}
    </button>
  );
}

/** wa.me 連結：md 內 text 參數係未 encode 嘅中文——copy 時轉返正式 encode 形態 */
function waLink(text: string): string {
  return "https://wa.me/85262322466?text=" + encodeURIComponent(text);
}

/** SHOW 三帖：按 `## SHOW-x｜` 切卡；每卡抽 wa.me 前綴做複製連結 */
function parseShowPosts(): { title: string; body: string; waText: string }[] {
  const out: { title: string; body: string; waText: string }[] = [];
  const parts = showPostsMd.split(/^## /m).filter((p) => /^SHOW-/.test(p));
  for (const p of parts) {
    const nl = p.indexOf("\n");
    const title = p.slice(0, nl).trim();
    const body = p.slice(nl + 1).trim();
    const m = body.match(/wa\.me\/85262322466\?text=([^`\s）)）]+)/);
    out.push({ title, body, waText: m ? m[1] : `SHOW${title.slice(5, 6)}-我想考下你哋個AI` });
  }
  return out;
}

/** 對話語料：按 `### D-x｜` 切卡 */
function parseDialogs(): { title: string; body: string }[] {
  const out: { title: string; body: string }[] = [];
  const parts = demoConversationsMd.split(/^### /m).filter((p) => /^D\d/.test(p));
  for (const p of parts) {
    const nl = p.indexOf("\n");
    out.push({ title: p.slice(0, nl).trim(), body: p.slice(nl + 1).trim() });
  }
  return out;
}

/** 30日日曆：抽 launch-campaign §一；markdown 表轉 HTML table，其餘行原樣排版 */
function CalendarSection() {
  const nodes = useMemo(() => {
    const s = launchCampaignMd.indexOf("## 一、");
    const e = launchCampaignMd.indexOf("\n## 二、");
    const sec = launchCampaignMd.slice(s, e > 0 ? e : undefined);
    const lines = sec.split("\n");
    const nodes: React.ReactNode[] = [];
    let table: string[][] = [];
    const flush = (key: string) => {
      if (!table.length) return;
      const rows = table.filter((r) => !r.every((c) => /^-{2,}$/.test(c.trim())));
      const [head, ...body] = rows;
      nodes.push(
        <table key={key} className="w-full text-xs border-collapse mb-3">
          <thead>
            <tr>
              {head.map((c, i) => (
                <th key={i} className="border border-slate-700 bg-slate-800/70 px-2.5 py-1.5 text-left text-slate-300">
                  {c.trim()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((r, ri) => (
              <tr key={ri}>
                {r.map((c, ci) => (
                  <td key={ci} className="border border-slate-800 px-2.5 py-1.5 text-slate-400 align-top">
                    {c.trim()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      table = [];
    };
    lines.forEach((line, i) => {
      if (line.startsWith("|")) {
        table.push(line.replace(/^\||\|$/g, "").split("|"));
        return;
      }
      flush("t" + i);
      if (!line.trim()) return;
      if (line.startsWith("### "))
        nodes.push(
          <h4 key={"h" + i} className="text-sm font-bold text-emerald-300 mt-5 mb-2">
            {line.slice(4)}
          </h4>
        );
      else if (line.startsWith("## "))
        nodes.push(
          <h3 key={"h" + i} className="text-base font-bold text-slate-200 mb-2">
            {line.slice(3)}
          </h3>
        );
      else if (line.startsWith(">"))
        nodes.push(
          <p key={"q" + i} className="text-[11px] text-slate-500 border-l-2 border-slate-700 pl-3 my-1 leading-relaxed">
            {line.replace(/^>\s?/, "")}
          </p>
        );
      else
        nodes.push(
          <p key={"p" + i} className="text-xs text-slate-300 font-medium mt-3 mb-1 leading-relaxed">
            {line}
          </p>
        );
    });
    flush("tend");
    return nodes;
  }, []);
  return <div>{nodes}</div>;
}

export default function MarketingAssetsPage() {
  const [tab, setTab] = useState<AssetTab>("show");
  const [openDoc, setOpenDoc] = useState<string | null>(null);
  const showPosts = useMemo(parseShowPosts, []);
  const dialogs = useMemo(parseDialogs, []);

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-100 mb-1">📣 行銷中心</h1>
      <p className="text-xs text-slate-500 mb-6">
        行銷成品＋訊息源集中管理——隨開隨用隨複製。資料源＝repo marketing/*.md（改檔重 build 即反映）。
      </p>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              tab === t.id
                ? "bg-emerald-600/20 border-emerald-500 text-emerald-300"
                : "bg-slate-950/40 border-slate-700/50 text-slate-400 hover:border-slate-500 hover:text-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "aipa" && (
        <div className="space-y-4">
          <div className="text-xs text-slate-500 bg-slate-900 border border-slate-800 rounded-lg p-3">
            投放規則：<span className="text-amber-300">邀請制口徑</span>（無定價／無未實作承諾／只講優先體驗）｜CTA＝阿Bot
            wa.me/85262322466＋預填字｜事實基準＝docs/marketing/aipa-positioning-notes.md
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3">
            <h3 className="text-base font-bold text-emerald-300">✦ AIPA（AI 個人助理）——材料索引</h3>
            <ul className="text-sm text-slate-300 space-y-2">
              <li>
                🌐 邀請制 Landing（四語）：
                <a className="text-sky-400 underline ml-1" href="https://wtsaibot.com/aipa/" target="_blank" rel="noopener">
                  wtsaibot.com/aipa/
                </a>
              </li>
              <li>
                🖼️ 海報×5＋og（1080×1080／1200×630）：repo <code className="text-amber-300">marketing/aipa/aipa-poster-*.png</code>＋
                <code className="text-amber-300">og-cover.png</code>（public/posters 更新後重 copy＋build 可下載）
              </li>
              <li>
                🎬 45 秒影片腳本：repo <code className="text-amber-300">marketing/aipa/video-script-45s.md</code>；MiniMax 旁白包
                <code className="text-amber-300">marketing/aipa/minimax/</code>
              </li>
              <li>
                🤝 EiD 組合出街包（「AI 員工團隊」post＋組合圖）：
                <code className="text-amber-300">marketing/aipa/eid-combo-pack.zip</code>
              </li>
            </ul>
            <CopyBtn text={aipaCopy} label="📋 複製整套文案包（copy-pack.md）" />
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <pre className="whitespace-pre-wrap text-xs text-slate-300 font-mono leading-relaxed max-h-[560px] overflow-auto">{aipaCopy}</pre>
          </div>
        </div>
      )}

      {tab === "show" && (
        <div className="space-y-4">
          <div className="text-xs text-slate-500 bg-slate-900 border border-slate-800 rounded-lg p-3">
            投放規則：<span className="text-rose-300">不提價</span>（199/499 唔好喺投放出現）｜歸因前綴
            SHOW1-3（同 FB1-4 分開量度）｜CTA 一律直达阿Bot wa.me（meta-demo 閉環）
          </div>
          {showPosts.map((p) => (
            <div key={p.title} className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <h3 className="text-base font-bold text-emerald-300">## {p.title}</h3>
                <div className="flex gap-2">
                  <CopyBtn text={`## ${p.title}\n\n${p.body}`} label="📋 複製文案" />
                  <CopyBtn text={waLink(p.waText)} label="🔗 複製 wa.me 連結" />
                </div>
              </div>
              <div className="flex gap-2 mb-3 text-[11px] font-mono">
                <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-400">歸因前綴：{p.waText.split("-")[0]}</span>
                <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-500 truncate max-w-md">{waLink(p.waText)}</span>
              </div>
              <pre className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-300 font-sans">{p.body}</pre>
            </div>
          ))}
        </div>
      )}

      {tab === "calendar" && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-slate-300">launch-campaign §一——每日渠道／素材／投自</h3>
            <CopyBtn text={launchCampaignMd.slice(launchCampaignMd.indexOf("## 一、"), launchCampaignMd.indexOf("\n## 二、"))} label="📋 複製整節" />
          </div>
          <CalendarSection />
        </div>
      )}

      {tab === "dialog" && (
        <div className="space-y-4">
          <div className="text-xs text-slate-500 bg-slate-900 border border-slate-800 rounded-lg p-3">
            8 組真實錄得對話（demo-conversations）——誠實紅線：對外只用實測原文。每組可整段複製做貼文素材。
          </div>
          {dialogs.map((d) => (
            <div key={d.title} className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <h3 className="text-base font-bold text-sky-300">{d.title}</h3>
                <CopyBtn text={`### ${d.title}\n\n${d.body}`} label="📋 複製" />
              </div>
              <pre className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-300 font-sans">{d.body}</pre>
            </div>
          ))}
        </div>
      )}

      {tab === "links" && (
        <div className="space-y-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">🔗 產品入口（投放落地点）</h3>
            {[
              ["Landing 官網", "https://wtsaibot.com/"],
              ["定價＋付款頁", "https://wtsaibot.com/pricing"],
              ["商戶門戶", "https://wtsaibot.com/merchant/"],
              ["開通精靈", "https://wtsaibot.com/setup"],
            ].map(([label, url]) => (
              <div key={url} className="flex items-center justify-between py-2 border-b border-slate-800/60 last:border-b-0">
                <span className="text-xs text-slate-400">{label}</span>
                <span className="flex items-center gap-2">
                  <span className="font-mono text-xs text-sky-300">{url}</span>
                  <CopyBtn text={url} label="複製" />
                </span>
              </div>
            ))}
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-slate-300 mb-1">🖼️ 海報素材（點開大圖・右鍵／長新圖另存）</h3>
            <p className="text-[11px] text-slate-500 mb-4">
              源檔 marketing/posters/（重生成後 copy 入 public/posters/ 再 build）。歸因前綴總表見 launch-campaign §六。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {POSTERS.map((p) => (
                <a
                  key={p.file}
                  href={`/posters/${p.file}`}
                  target="_blank"
                  rel="noopener"
                  className="block bg-slate-950/40 border border-slate-700/40 rounded-lg overflow-hidden hover:border-emerald-600/60 transition-colors"
                >
                  <img src={`/posters/${p.file}`} alt={p.label} className="w-full h-auto" loading="lazy" />
                  <div className="px-3 py-2 text-[11px] text-slate-400 flex items-center justify-between">
                    <span>{p.label}</span>
                    <span className="text-emerald-500/70 font-mono">⤓ png</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "pitch" && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-emerald-400 mb-3">🏆 WA AI CSR 絕對優勢（訊息主軸）</h2>
            <div className="mb-3 p-4 bg-emerald-900/20 border border-emerald-800/40 rounded-xl">
              <p className="text-emerald-300 text-sm font-medium">
                「AI 幫你擋住 80% 查詢；擋唔住嘅，即刻轉返俾你——仲會將你嘅回覆學埋，下次唔使你再出手。」
              </p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {ADVANTAGES.map((a) => (
                <div key={a.title} className="bg-slate-900 rounded-xl border border-slate-800 p-4">
                  <div className="text-sm font-semibold text-slate-100 mb-1">
                    {a.icon} {a.title}
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">{a.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-amber-400 mb-1">📋 交付物重做評估</h2>
            <p className="text-xs text-slate-500 mb-3">
              原 2026-08-16 評估，2026-09-08 按現況重評——三項已完成落地，剩兩項待做（demo 補錄 learning loop 實錄＞45s 影片）。
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-slate-400 uppercase bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="px-4 py-3">交付物</th>
                    <th className="px-4 py-3">現況</th>
                    <th className="px-4 py-3">詳情／下一步</th>
                  </tr>
                </thead>
                <tbody>
                  {REFRESH_PLAN.map((r) => (
                    <tr key={r.file} className="bg-slate-900/50 border-b border-slate-800">
                      <td className="px-4 py-3 font-mono text-xs text-slate-300">{r.file}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${LEVEL_STYLE[r.level].badge}`}>
                          {LEVEL_STYLE[r.level].label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">{r.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              優先級：demo 補錄 learning loop 實錄（王牌素材）＞ 45s 影片製作；其餘已落地或照用。
            </p>
          </div>
        </div>
      )}

      {tab === "docs" && (
        <div>
          <h2 className="text-lg font-semibold text-slate-200 mb-3">📄 交付物原文庫（10 份，全部可展開）</h2>
          <div className="space-y-2">
            {DOCS.map((d) => (
              <div key={d.key} className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
                <div className="flex items-center">
                  <button
                    onClick={() => setOpenDoc(openDoc === d.key ? null : d.key)}
                    className="flex-1 flex items-center justify-between px-4 py-3 text-left hover:bg-slate-800/60 transition-colors"
                  >
                    <span className="text-sm text-slate-200">{d.label}</span>
                    <span className="text-xs text-slate-500 font-mono">
                      marketing/{d.file} {openDoc === d.key ? "▲" : "▼"}
                    </span>
                  </button>
                  <div className="pr-3">
                    <CopyBtn text={d.content} label="📋 複製全文" />
                  </div>
                </div>
                {openDoc === d.key && (
                  <pre className="px-4 pb-4 pt-1 text-xs text-slate-400 whitespace-pre-wrap max-h-[32rem] overflow-auto border-t border-slate-800">
                    {d.content}
                  </pre>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            HTML 交付物（aibot-landing.html、video-animatic.html）為自足單檔，請直接喺 repo 開啟或部署到 Pages。
          </p>
        </div>
      )}
    </div>
  );
}
